import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { refreshSessionData } from "../utils/refreshSessionData";

// Always refresh slices when landing on these pages so headers / dashboards
// always reflect up-to-date data regardless of how the user arrived.
const ALWAYS_REFRESH_PATHS = ["/home", "/agent/dashboard", "/agent"];

function isAlwaysRefreshPath(pathname) {
  if (pathname.startsWith("/agent/accounts/chart")) {
    return true;
  }
  return ALWAYS_REFRESH_PATHS.some(
    (p) => pathname === p || pathname === `${p}/`
  );
}

export default function useRefreshUserOnRouteChange() {
  const dispatch = useDispatch();
  const location = useLocation();
  const userId = useSelector((s) => s.userStore.data?._id);
  // Skip the very first route change after userId becomes available so we
  // don't race with AppRoot's initial load — EXCEPT for dashboard/home pages
  // where fresh data is most important.
  const skipNextRef = useRef(true);

  useEffect(() => {
    if (!userId) {
      // Not logged in yet — arm the skip for when we do log in.
      skipNextRef.current = true;
      return;
    }

    // Always refresh on home/dashboard pages.
    if (isAlwaysRefreshPath(location.pathname)) {
      refreshSessionData(dispatch).catch((err) => {
        console.error("Slice refresh on home/dashboard failed:", err);
      });
      // Disarm the skip so the next regular navigation also refreshes.
      skipNextRef.current = false;
      return;
    }

    // For all other pages, skip exactly once after userId becomes set
    // (avoids double-fetching with AppRoot's initial load or Login's fetch).
    if (skipNextRef.current) {
      skipNextRef.current = false;
      return;
    }

    refreshSessionData(dispatch).catch((err) => {
      console.error("Slice refresh on route change failed:", err);
    });
  }, [location.pathname, userId, dispatch]);
}
